import React, { useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import * as LoyaltyService from '~/services/LoyaltyService';

import ApiError from '~/types/ApiError';
import {
  CUSTOM_RULE_PURPOSE_OPTIONS,
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRule,
  type CustomRuleInput,
  type CustomRulePurpose,
  type CustomRuleType,
  MAX_END_DATE,
  MIN_START_DATE,
  type MatchConfig,
  POINTS_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  type PointsType,
  type ProductType,
} from '~/types/loyaltyContract';

import MatchConfigBuilder from './MatchConfigBuilder';
import { parseCustomRuleApiErrors } from './customRuleApiErrors';
import {
  type BatchCreateFailureSummary,
  buildBatchCustomRules,
  getBatchCreateFailureSummary,
  parseBatchIdentifierValues,
} from './customRuleBatch';
import {
  CUSTOM_RULE_IDENTIFIER_LABELS,
  type CustomRuleIdentifierType,
  isCustomRuleTextIdentifier,
  validateCustomRuleFields,
} from './customRuleFieldValidation';
import { validateMatchConfig } from './matchConfigValidation';

interface Props {
  themeColor: string;
  open: boolean;
  onClose: (newlyCreatedRule?: CustomRule) => void;
}

export default function CreateCustomRuleDialog({ themeColor, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Validation errors from API
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [selectedIdentifierType, setSelectedIdentifierType] = useState<CustomRuleIdentifierType>('earnKey');
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchProductTypes, setBatchProductTypes] = useState<Array<ProductType>>([]);
  const [batchFailureSummary, setBatchFailureSummary] = useState<BatchCreateFailureSummary | null>(null);

  const identifierOptions = Object.entries(CUSTOM_RULE_IDENTIFIER_LABELS).map(([value, label]) => ({
    value: value as CustomRuleIdentifierType,
    label,
  }));

  const [formData, setFormData] = useState<Partial<CustomRuleInput>>({
    type: 'BONUS_POINTS',
    purpose: 'ESTIMATION_ONLY',
    pointsType: 'BONUS_POINTS',
    productType: null,
    name: '',
    startDate: '',
    endDate: null,
  });

  // Optional flexible-matching config (off by default; null = section disabled).
  const [matchConfig, setMatchConfig] = useState<MatchConfig | null>(null);

  const createMutation = useMutation({
    mutationFn: (rule: CustomRuleInput) => LoyaltyService.createCustomRule({ rule }),
    onSuccess: (response) => {
      enqueueSnackbar('Custom rule created successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
      setValidationErrors({}); // Clear any previous validation errors
      const newRule = response?.result?.createdRule;
      onClose(newRule); // Pass the full rule object back to render it
    },
    onError: (error: ApiError) => {
      if (error.name === 'ValidationError' && error.status === 400) {
        const { errors, hadUnparsed } = parseCustomRuleApiErrors(error.errors);
        setValidationErrors(errors);
        // Always surface a visible message: prefer the per-field summary, but fall
        // back to the server message when nothing parsed into a field (so the user
        // never sees a silent failure).
        const hasFieldErrors = Object.keys(errors).length > 0;
        if (hasFieldErrors && !hadUnparsed) {
          enqueueSnackbar('Please fix validation errors', { variant: 'error' });
        } else {
          enqueueSnackbar(error.message || 'Validation error', { variant: 'error' });
        }
      } else {
        enqueueSnackbar(`Error creating custom rule: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });

  const createBatchMutation = useMutation({
    mutationFn: ({ rules }: { rules: Array<CustomRuleInput>; identifierValues: Array<string> }) =>
      LoyaltyService.createCustomRulesBatch({ rules }),
    onSuccess: (response) => {
      const countCreatedRules = response?.result?.countCreatedRules ?? 0;
      enqueueSnackbar(`Created ${countCreatedRules} custom rules`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
      setValidationErrors({});
      setBatchFailureSummary(null);
      onClose();
    },
    onError: (error: ApiError, variables) => {
      setBatchFailureSummary(getBatchCreateFailureSummary(error.errors, variables.identifierValues, error.message));
      if (error.name === 'ValidationError' && error.status === 400) {
        const { errors } = parseCustomRuleApiErrors(error.errors);
        setValidationErrors(errors);
      }
    },
  });

  const isTextIdentifier = isCustomRuleTextIdentifier(selectedIdentifierType);
  const isProductTypeIdentifier = selectedIdentifierType === 'productType';
  const canUseBatchMode = isTextIdentifier || isProductTypeIdentifier;
  const isCreating = createMutation.isPending || createBatchMutation.isPending;
  const currentIdentifierInput = isTextIdentifier ? String(formData[selectedIdentifierType] ?? '') : '';
  const batchIdentifierValues = useMemo(() => {
    if (!isBatchMode) {
      return [];
    }
    if (isProductTypeIdentifier) {
      return batchProductTypes;
    }
    if (isTextIdentifier) {
      return parseBatchIdentifierValues(currentIdentifierInput);
    }
    return [];
  }, [batchProductTypes, currentIdentifierInput, isBatchMode, isProductTypeIdentifier, isTextIdentifier]);
  const batchIdentifierCount = batchIdentifierValues.length;

  const handleClose = (): void => {
    setFormData({
      type: 'BONUS_POINTS_ONLY',
      purpose: 'ESTIMATION_AND_EARN',
      pointsType: 'BONUS_POINTS',
      productType: null,
      name: '',
      startDate: '',
      endDate: null,
    });
    setSelectedIdentifierType('earnKey');
    setIsBatchMode(false);
    setBatchProductTypes([]);
    setBatchFailureSummary(null);
    setMatchConfig(null);
    setValidationErrors({});
    onClose();
  };

  const clearSelectedIdentifier = (identifierType: CustomRuleIdentifierType): void => {
    setFormData((current) => ({
      ...current,
      [identifierType]: undefined,
    }));
    setValidationErrors((current) => {
      const next = { ...current };
      delete next[identifierType];
      return next;
    });
  };

  const toggleBatchMode = (): void => {
    if (!canUseBatchMode) {
      return;
    }

    setIsBatchMode((current) => !current);
    setBatchFailureSummary(null);
    setBatchProductTypes([]);
    clearSelectedIdentifier(selectedIdentifierType);
  };

  const handleSubmit = (): void => {
    setBatchFailureSummary(null);

    const fieldErrors = validateCustomRuleFields({
      name: formData.name,
      startDate: formData.startDate ?? undefined,
      type: formData.type,
      selectedIdentifierType,
      identifierValue: formData[selectedIdentifierType],
      batchIdentifierValues: isBatchMode ? batchIdentifierValues : undefined,
      pointsValue: formData.pointsValue ?? undefined,
      pointsMultiplier: formData.pointsMultiplier ?? undefined,
    });

    // Validate matchConfig client-side (mirrors server rules) when the section is enabled.
    const matchErrors = matchConfig ? validateMatchConfig(matchConfig) : {};

    const allErrors = { ...fieldErrors, ...matchErrors };
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      enqueueSnackbar('Please fix the highlighted fields before creating the rule', { variant: 'error' });
      return;
    }

    // Narrow the required fields for the payload now that validation passed.
    if (!formData.name || !formData.startDate || !formData.type || !formData.purpose || !formData.pointsType) {
      return;
    }

    const rule: CustomRuleInput = {
      type: formData.type,
      purpose: formData.purpose,
      pointsType: formData.pointsType,
      name: formData.name,
      // Convert to Sydney timezone midnight and then to ISO 8601 UTC
      startDate: dayjs.tz(formData.startDate, 'Australia/Sydney').startOf('day').toISOString(),
      endDate: formData.endDate
        ? dayjs.tz(formData.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString()
        : null,
      // Only set the selected identifier field, all others are null
      offerId: selectedIdentifierType === 'offerId' ? formData.offerId || null : null,
      packageId: selectedIdentifierType === 'packageId' ? formData.packageId || null : null,
      ratePlanId: selectedIdentifierType === 'ratePlanId' ? formData.ratePlanId || null : null,
      earnKey: selectedIdentifierType === 'earnKey' ? formData.earnKey || null : null,
      promoKey: selectedIdentifierType === 'promoKey' ? formData.promoKey || null : null,
      productType: selectedIdentifierType === 'productType' ? formData.productType || null : null,
      region: null, // Hidden field, rarely used
      identifierDescription: formData.identifierDescription || null,
      pointsCap: null, // Always null based on server rules
      pointsValue:
        formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS'
          ? (formData.pointsValue ?? null)
          : null,
      pointsMultiplier: formData.type === 'MULTIPLE_ON_BASE_POINTS' ? (formData.pointsMultiplier ?? null) : null,
      pointsRatio: null, // Always null based on server rules
      pointsValidDays: null, // Hidden field, rarely used
      pointsValidMonths: formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36,
      // Omit the key entirely when the section is disabled (matchConfig is optional).
      ...(matchConfig ? { matchConfig } : {}),
    };

    if (isBatchMode) {
      const rules = buildBatchCustomRules(rule, selectedIdentifierType, batchIdentifierValues);
      createBatchMutation.mutate({ rules, identifierValues: batchIdentifierValues });
      return;
    }

    createMutation.mutate(rule);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={isBatchMode && isTextIdentifier ? 'lg' : 'md'} fullWidth>
      <DialogTitle>Create New Custom Rule</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (validationErrors.name) {
                  setValidationErrors({ ...validationErrors, name: '' });
                }
              }}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              required
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e: SelectChangeEvent<CustomRuleType>) => {
                    const newType = e.target.value;
                    // Type guard to ensure we have a valid CustomRuleType
                    if (
                      newType === 'BONUS_POINTS_ONLY' ||
                      newType === 'MULTIPLE_ON_BASE_POINTS' ||
                      newType === 'BONUS_POINTS'
                    ) {
                      setFormData({
                        ...formData,
                        type: newType,
                        // Auto-set purpose based on type
                        purpose: newType === 'BONUS_POINTS_ONLY' ? 'ESTIMATION_AND_EARN' : 'ESTIMATION_ONLY',
                        pointsCap: null,
                        pointsValue:
                          newType === 'BONUS_POINTS_ONLY' || newType === 'BONUS_POINTS'
                            ? formData.pointsValue
                            : undefined,
                        pointsMultiplier: newType === 'MULTIPLE_ON_BASE_POINTS' ? formData.pointsMultiplier : undefined,
                        pointsRatio: null,
                        // Reset pointsType to BONUS_POINTS (valid for all types) when type changes
                        pointsType: 'BONUS_POINTS',
                      });
                    }
                  }}
                >
                  {CUSTOM_RULE_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Purpose</InputLabel>
                <Select
                  value={formData.purpose}
                  label="Purpose"
                  disabled
                  onChange={(e: SelectChangeEvent<CustomRulePurpose>) =>
                    setFormData({ ...formData, purpose: e.target.value })
                  }
                >
                  {CUSTOM_RULE_PURPOSE_OPTIONS.map((purpose) => (
                    <MenuItem key={purpose} value={purpose}>
                      {purpose}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Points Type</InputLabel>
                <Select
                  value={formData.pointsType}
                  label="Points Type"
                  onChange={(e: SelectChangeEvent<PointsType>) =>
                    setFormData({ ...formData, pointsType: e.target.value })
                  }
                >
                  {POINTS_TYPE_OPTIONS.filter((type) => {
                    // only allowed BONUS_POINTS, BONUS_STATUS_CREDITS for new rules
                    return type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS';
                  }).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Identifier Type</InputLabel>
                <Select
                  value={selectedIdentifierType}
                  label="Identifier Type"
                  onChange={(e: SelectChangeEvent<CustomRuleIdentifierType>) => {
                    const newType = e.target.value;

                    if (
                      newType === 'offerId' ||
                      newType === 'packageId' ||
                      newType === 'ratePlanId' ||
                      newType === 'earnKey' ||
                      newType === 'promoKey' ||
                      newType === 'productType'
                    ) {
                      setSelectedIdentifierType(newType);
                      setIsBatchMode(false);
                      setBatchProductTypes([]);
                      setBatchFailureSummary(null);
                      setFormData({
                        ...formData,
                        offerId: undefined,
                        packageId: undefined,
                        ratePlanId: undefined,
                        earnKey: undefined,
                        promoKey: undefined,
                        productType: undefined,
                      });
                      const newErrors = { ...validationErrors };
                      delete newErrors.offerId;
                      delete newErrors.packageId;
                      delete newErrors.ratePlanId;
                      delete newErrors.earnKey;
                      delete newErrors.promoKey;
                      delete newErrors.productType;
                      setValidationErrors(newErrors);
                    }
                  }}
                >
                  {identifierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedIdentifierType === 'productType' ? (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {isBatchMode ? (
                    <FormControl fullWidth required error={!!validationErrors.productType}>
                      <InputLabel>Product Types</InputLabel>
                      <Select
                        multiple
                        value={batchProductTypes}
                        label="Product Types"
                        onChange={(event) => {
                          const value = event.target.value;
                          const selectedValues = typeof value === 'string' ? value.split(',') : value;
                          setBatchFailureSummary(null);
                          setBatchProductTypes(
                            selectedValues.filter((type): type is ProductType =>
                              (PRODUCT_TYPE_OPTIONS as ReadonlyArray<string>).includes(type),
                            ),
                          );
                          if (validationErrors.productType) {
                            setValidationErrors({ ...validationErrors, productType: '' });
                          }
                        }}
                        renderValue={(selected) => selected.join(', ')}
                      >
                        {PRODUCT_TYPE_OPTIONS.map((type) => (
                          <MenuItem key={type} value={type}>
                            <Checkbox checked={batchProductTypes.includes(type)} />
                            <ListItemText primary={type} />
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography
                        variant="caption"
                        color={validationErrors.productType ? 'error' : 'text.secondary'}
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {validationErrors.productType ||
                          `${batchIdentifierCount} product type${batchIdentifierCount === 1 ? '' : 's'} selected. Select at least one.`}
                      </Typography>
                    </FormControl>
                  ) : (
                    <FormControl fullWidth required error={!!validationErrors.productType}>
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={formData.productType || ''}
                        label="Product Type"
                        onChange={(e: SelectChangeEvent<string>) => {
                          setFormData({ ...formData, productType: e.target.value as ProductType });

                          if (validationErrors.productType) {
                            setValidationErrors({ ...validationErrors, productType: '' });
                          }
                        }}
                      >
                        {PRODUCT_TYPE_OPTIONS.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.productType && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {validationErrors.productType}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                  <Box>
                    <Button variant="outlined" onClick={toggleBatchMode}>
                      {isBatchMode ? 'Disable batch mode' : 'Enable batch mode'}
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <TextField
                    label={identifierOptions.find((opt) => opt.value === selectedIdentifierType)?.label}
                    value={formData[selectedIdentifierType] || ''}
                    onChange={(e) => {
                      setBatchFailureSummary(null);
                      setFormData({ ...formData, [selectedIdentifierType]: e.target.value });

                      if (validationErrors[selectedIdentifierType]) {
                        setValidationErrors({ ...validationErrors, [selectedIdentifierType]: '' });
                      }
                    }}
                    error={!!validationErrors[selectedIdentifierType]}
                    helperText={
                      validationErrors[selectedIdentifierType] ||
                      (isBatchMode
                        ? `${batchIdentifierCount} unique values parsed. Paste comma-separated or newline-separated values.`
                        : undefined)
                    }
                    required
                    fullWidth
                    multiline={isBatchMode}
                    minRows={isBatchMode ? 12 : undefined}
                    maxRows={isBatchMode ? 24 : undefined}
                    sx={
                      isBatchMode
                        ? {
                            '& .MuiInputBase-inputMultiline': {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                              overflow: 'auto',
                            },
                          }
                        : undefined
                    }
                  />
                  <Box>
                    <Button variant="outlined" onClick={toggleBatchMode}>
                      {isBatchMode ? 'Disable batch mode' : 'Enable batch mode'}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Stack>

            {isBatchMode && batchFailureSummary && (
              <Alert severity="error">
                <Typography variant="subtitle2" component="div">
                  Batch creation failed ({batchFailureSummary.failedValues.length} values)
                </Typography>
                {batchFailureSummary.message && (
                  <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                    {batchFailureSummary.message}
                  </Typography>
                )}
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  {batchFailureSummary.hasPerValueDetails
                    ? 'No rules were created. Fix these invalid submitted values and try again:'
                    : 'No rules were created. The API returned only a batch-level failure, so the invalid input row could not be identified:'}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    maxHeight: 360,
                    mb: 1,
                    mt: 1,
                    overflow: 'auto',
                    pl: 3,
                    py: 1,
                  }}
                >
                  {batchFailureSummary.failedValues.map((item) => (
                    <Box component="li" key={item.value}>
                      <Typography variant="body2" component="span">
                        {item.value}: {item.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {batchFailureSummary.hasPerValueDetails && (
                  <Typography variant="body2">Other submitted values were not marked as failed by the API.</Typography>
                )}
                {!batchFailureSummary.hasPerValueDetails && (
                  <Typography variant="body2">
                    Because batch creation is all-or-nothing, valid values in this request were not created either.
                    Split the values into smaller batches if the backend does not return an indexed error.
                  </Typography>
                )}
              </Alert>
            )}

            <Stack direction="row" spacing={2}>
              {(formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS') && (
                <TextField
                  label="Points Value"
                  type="number"
                  value={formData.pointsValue ?? ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({
                      ...formData,
                      pointsValue: value === 0 ? undefined : value,
                    });

                    if (validationErrors.pointsValue) {
                      setValidationErrors({ ...validationErrors, pointsValue: '' });
                    }
                  }}
                  error={!!validationErrors.pointsValue}
                  helperText={validationErrors.pointsValue}
                  required
                  fullWidth
                />
              )}
              {formData.type === 'MULTIPLE_ON_BASE_POINTS' && (
                <TextField
                  label="Points Multiplier"
                  type="number"
                  value={formData.pointsMultiplier ?? ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({
                      ...formData,
                      pointsMultiplier: value === 0 ? undefined : value,
                    });

                    if (validationErrors.pointsMultiplier) {
                      setValidationErrors({ ...validationErrors, pointsMultiplier: '' });
                    }
                  }}
                  error={!!validationErrors.pointsMultiplier}
                  helperText={validationErrors.pointsMultiplier}
                  required
                  fullWidth
                />
              )}
            </Stack>

            <TextField
              label="Points Valid Months"
              type="number"
              value={formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36}
              onChange={(e) => {
                const value = Number(e.target.value);
                setFormData({
                  ...formData,
                  pointsValidMonths: value === 0 ? undefined : value,
                });

                if (validationErrors.pointsValidMonths) {
                  setValidationErrors({ ...validationErrors, pointsValidMonths: '' });
                }
              }}
              disabled
              error={!!validationErrors.pointsValidMonths}
              helperText={
                formData.type === 'BONUS_POINTS_ONLY'
                  ? 'Fixed at 6 months for Bonus Points Only type'
                  : 'Fixed at 36 months for this type'
              }
              fullWidth
            />

            <TextField
              label="Identifier Description"
              value={formData.identifierDescription || ''}
              onChange={(e) => {
                setFormData({ ...formData, identifierDescription: e.target.value });

                if (validationErrors.identifierDescription) {
                  setValidationErrors({ ...validationErrors, identifierDescription: '' });
                }
              }}
              error={!!validationErrors.identifierDescription}
              helperText={validationErrors.identifierDescription || 'Description for this identifier'}
              fullWidth
            />

            <MatchConfigBuilder
              value={matchConfig}
              onChange={setMatchConfig}
              errors={validationErrors}
              anchorField={selectedIdentifierType}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => {
                  setFormData({ ...formData, startDate: e.target.value });

                  if (validationErrors.startDate) {
                    setValidationErrors({ ...validationErrors, startDate: '' });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: MIN_START_DATE,
                  max: MAX_END_DATE,
                }}
                error={!!validationErrors.startDate}
                helperText={`Must be after today's date`}
                required
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate || ''}
                onChange={(e) => {
                  setFormData({ ...formData, endDate: e.target.value || null });

                  if (validationErrors.endDate) {
                    setValidationErrors({ ...validationErrors, endDate: '' });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formData.startDate || MIN_START_DATE,
                  max: MAX_END_DATE,
                }}
                error={!!validationErrors.endDate}
                helperText={
                  validationErrors.endDate || `Optional - Rule will be valid through the end of this date (inclusive)`
                }
                fullWidth
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: themeColor }} onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button sx={{ backgroundColor: themeColor }} onClick={handleSubmit} variant="contained" disabled={isCreating}>
          {isCreating ? 'Creating...' : isBatchMode ? 'Create rules' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
